// Import Libraries
import React, { createContext, useEffect, useState, useMemo } from 'react';

// Create a context
const SessionContext = createContext();

// Create a context provider component
export const SessionProvider = ({ children }) => {
  const [generalModalDetails, setGeneralModalDetails] = useState({
    open: false,
    header: '',
    message: '',
    wide: false,
  });
  const [isMobile, setIsMobile] = useState(false);
  const [visitedWelcome, setVisitedWelcome] = useState(false);
  const [blockNav, setBlockNav] = useState(false);
  const [welcomeValidationTrigger, setWelcomeValidationTrigger] = useState(0);
  // Set by SharedExportViewer while an exported PDF is actually on screen, so
  // HeaderBar/FooterBar can hide the standard chrome only for that state (not
  // for the loading/expired-link states of the same /view/ route).
  const [isViewingPdf, setIsViewingPdf] = useState(false);
  // Set by AccessRequired while a signed-out visitor is looking at it (either
  // via the /accessRequired route or rendered inline, e.g. scriptView bouncing
  // a logged-out visitor off a shared script link), so HeaderBar can hide
  // Help/Feedback -- both point at authenticated-only resources.
  const [isAccessRequired, setIsAccessRequired] = useState(false);

  // Shown whenever a user with an incomplete Welcome screen tries to navigate
  // away (Help, Feedback, Profile, FAQ, Contact Us). Also signals the Welcome
  // screen to outline its still-empty required fields in red.
  const notifyIncompleteWelcome = () => {
    setGeneralModalDetails({
      open: true,
      header: <span>We need more information</span>,
      message: (
        <div className="Padded">
          We need to collect just a little bit more information before you can continue.
          <br />
          <br />
          <div className="fullWidthContainer Padded">
            <span className="OneHalfFlex" />
            <button
              className="OneHalfFlex buttonClone"
              onClick={() =>
                setGeneralModalDetails({
                  open: false,
                  header: null,
                  message: null,
                })
              }
            >
              Ok
            </button>
          </div>
        </div>
      ),
    });
    setWelcomeValidationTrigger((prev) => prev + 1);
  };

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);

    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  // Memoize the context value
  const contextValue = useMemo(
    () => ({
      generalModalDetails,
      setGeneralModalDetails,
      isMobile,
      visitedWelcome,
      setVisitedWelcome,
      blockNav,
      setBlockNav,
      welcomeValidationTrigger,
      notifyIncompleteWelcome,
      isViewingPdf,
      setIsViewingPdf,
      isAccessRequired,
      setIsAccessRequired,
    }),
    [
      generalModalDetails,
      isMobile,
      visitedWelcome,
      blockNav,
      welcomeValidationTrigger,
      isViewingPdf,
      isAccessRequired,
    ],
  );

  return <SessionContext.Provider value={contextValue}>{children}</SessionContext.Provider>;
};

export default SessionContext;
